/**
 * Every route path in the app, in one place. Components and nav config
 * reference these constants — never a string literal.
 *
 * The list stays short by design (ADR-0008): one route per module, with every
 * detail/create/edit surface opening as a drawer over its list, addressed by
 * `?record=`. The only exceptions are the unauthenticated and blocking
 * surfaces below, which have no list to sit over.
 *
 * The goods receipt was the one document that had its own pages (ADR-0019).
 * It no longer does — entry and the posted document are drawers over
 * `/goods-receipts`, addressed by `?record=new` and `?record=<id>` like
 * everything else (ADR-0021). The two old paths are **kept as redirects**:
 * they are live URLs, one of them is in a demo script, and neither may 404.
 */
export const ROUTES = {
  dashboard: '/',
  /** Public. The only unauthenticated surface. */
  login: '/login',
  /** Authenticated but outside the app shell — blocking (Story I). */
  changePassword: '/change-password',
  users: '/users',
  roles: '/roles',

  // --- feature 002 (002/design.md §6.2)
  rawMaterials: '/raw-materials',
  vendors: '/vendors',
  transporters: '/transporters',
  goodsReceipts: '/goods-receipts',
  /**
   * **Legacy.** Redirects to `/goods-receipts?record=new`, preserving the rest
   * of the query (`?workspace=`, filters). Static, and declared before the
   * `:id` route so it is never parsed as a receipt id — React Router ranks a
   * static segment above a dynamic one, so this is belt and braces rather than
   * the mechanism.
   */
  goodsReceiptNew: '/goods-receipts/new',
  /**
   * **Legacy.** Redirects to `/goods-receipts?record=<id>`. A pattern, for the
   * router; build a concrete URL with `goodsReceiptPath`, which returns the
   * canonical drawer URL rather than this one.
   */
  goodsReceiptDetail: '/goods-receipts/:id',
  /**
   * --- feature 012 (012/design.md §7.2).
   *
   * **A standalone page, not a drawer**, and the one deliberate exception to
   * ADR-0021 in this feature: AC-9 requires the UI chrome excluded from the
   * printed output, and a print stylesheet over a full-viewport overlay is
   * fragile at exactly the moment it matters. Here the nav and the buttons are
   * absent from the DOM rather than hidden, so there is nothing for a print
   * rule to have to defeat.
   *
   * **Declared above the shell route — belt and braces, not the mechanism.**
   * Rank already protects it: React Router 7 scores `/goods-receipts/:id/print`
   * at 28 against 16 for `/goods-receipts/:id/*` and 12 for `/goods-receipts/*`,
   * and rank is resolved before declaration order. The `goodsReceiptDetail`
   * redirect is a *leaf* `/goods-receipts/:id`, which cannot match a
   * three-segment path at all. Order decides in exactly one arrangement: a
   * shell *layout* route `/goods-receipts/:id` with a nested `{ path: 'print' }`
   * child, which has an identical full path and an identical score, so the tie
   * falls to declaration order. **Do not move this below the shell.**
   */
  goodsReceiptPrint: '/goods-receipts/:id/print',
  stock: '/stock',
  /** --- feature 012: purchase returns, their own list (A-12 — never merged into receipts). */
  purchaseReturns: '/purchase-returns',
  /** System configuration, reached from Administration — not a list record. */
  plantProfile: '/settings/plant',
  /**
   * --- feature 021 (021/design.md §6.1). The plant mould setting — one row, a page and not a
   * drawer, for Plant Profile's reason: it is configuration, not a record in a list.
   */
  mouldSetting: '/settings/mould',

  /**
   * --- feature 010 (010/design.md §7.1).
   *
   * **One route, drawers over it.** The group editor is a full-size *drawer*
   * and not a page: the brief cited ADR-0019 ("a component grid does not
   * survive a 720px panel"), which ADR-0021 superseded the same day by
   * answering that objection with `size="full"` plus the composer pattern. A
   * page here would be the only page left in the app and would lose
   * Back-closes-the-drawer.
   *
   * Tabs (`?tab=groups|components`), the record drawer (`?record=`) and the
   * list filters are all query parameters over this one path.
   */
  taxConfiguration: '/tax-configuration',

  // --- feature 003 (003/design.md §7.1). Both in the existing `manufacturing`
  // workspace — finished goods and recipes are physical-goods screens, which is
  // that workspace's organising principle, so no new workspace (ADR-0012).
  //
  // Two routes and no sub-pages: every detail, create, edit, version and
  // preview surface is a drawer over one of these two lists, addressed by
  // `?record=` and this feature's own `?tab=`, `?version=`, `?compare=` and
  // `?preview=` (ADR-0008, ADR-0021 — **not** the superseded ADR-0019, which
  // 010's design cited by mistake and had to correct).
  finishedGoods: '/finished-goods',
  recipes: '/recipes',

  /**
   * --- feature 004 (004/design.md §6.2).
   *
   * **One route for the whole module.** The list, the entry drawer
   * (`?record=new`), the PO document (`?record=<id>`), a draft edit
   * (`&mode=edit`), an amendment (`&mode=amend`), the revision history
   * (`&revisions=1`) and a superseded revision (`&revision=<n>`) are all query
   * parameters over this one path (ADR-0008, ADR-0021) — **not** the superseded
   * ADR-0019, which would have made a multi-line document its own page.
   *
   * Story N (reorder alerts) is `?tab=reorder-alerts` over this path too, for
   * 010's reason: it is a different view of the same module, not a different
   * module.
   */
  purchaseOrders: '/purchase-orders',
  /**
   * **A standalone page, not a drawer** — the one deliberate exception to
   * ADR-0021 in this feature, for 012's reason: AC-B4 requires the UI chrome
   * excluded from the printed output, and a print stylesheet over a
   * full-viewport overlay is fragile at exactly the moment it matters. Here the
   * nav and the buttons are absent from the DOM rather than hidden.
   *
   * **Declared above the shell route — belt and braces, not the mechanism**
   * (design §6.2 states the ordering as a hard constraint). Rank already
   * protects it: React Router 7 scores `/purchase-orders/:id/print` at 28
   * against 16 for `/purchase-orders/:id/*` and 12 for `/purchase-orders/*`, and
   * rank is resolved before declaration order; a *leaf* `/purchase-orders/:id`
   * cannot match a three-segment path at all. Order decides in exactly one
   * arrangement: a shell *layout* route `/purchase-orders/:id` with a nested
   * `{ path: 'print' }` child, which has an identical full path and an identical
   * score, so the tie falls to declaration order. **Do not move this below the
   * shell.**
   */
  purchaseOrderPrint: '/purchase-orders/:id/print',

  /**
   * --- feature 016 (016/design.md §7.1), amended by 021.
   *
   * The plan **composer** and the plan document are drawers over `/manufacturing-plans`,
   * addressed by `?record=new` and `?record=<id>`. **Both are `AppDrawer size="wide"`** — the
   * manager's ruling of 2026-10-07, an exception to ADR-0021's composer-is-`full` default recorded
   * in that ADR's Amendments. Never cite ADR-0019, which 0021 superseded the day it was written.
   *
   * 016's `/moulds` route is **gone** (021 R-1: no per-mould records). It falls through to the
   * NotFound route, which is Story A's "routes return 404".
   */
  manufacturingPlans: '/manufacturing-plans',

  /**
   * --- feature 005 (005/design.md §6.1).
   *
   * **One route, drawers over it, and ZERO new nav slots** (OQ-11). The composer
   * (`?record=new`), the posted document (`?record=<id>`) and the reversal sheet
   * (`&reverse=1`) are all query parameters over this one path (ADR-0008,
   * ADR-0021 — **not** the superseded ADR-0019). Back closes the drawer rather
   * than leaving the module.
   *
   * **`nav-items.tsx` is not touched.** The Manufacturing bar is at capacity at
   * 1024 with its existing seven items, and an antd submenu popup is a measured
   * non-option in this header. The list is reached from the `SegmentedControl`
   * on `/manufacturing-plans` (Plans · Production Runs), and from a plan's
   * progress block as `/production-runs?planId=<id>`.
   *
   * **A separate path and not `?tab=runs` over `/manufacturing-plans`**: both
   * surfaces already use `?record=<id>`, and one `record` parameter serving two
   * record *families* over one path means a stale bookmark resolves against the
   * wrong family and 404s silently, because a uuid is a uuid.
   */
  productionRuns: '/production-runs',

  /**
   * --- feature 011 (011/design.md §8.2). The payment register, in the **finance** workspace.
   * Entry (`?record=new`) and the posted voucher (`?record=<id>`) are drawers over it.
   */
  payments: '/payments',
  /** The chart of accounts and the payment instruments (011 F5/F4), finance workspace. */
  accountHeads: '/account-heads',
  paymentMethods: '/payment-methods',

  /**
   * --- feature 017 (017/design.md §7.2 screen 3).
   *
   * **Finance workspace, `goods_receipt.read` guard** — no new permission code
   * (§5.1, §8.1). The register is the year's TCS overview; reading it requires
   * the same grant as reading the receipts that contain TCS.
   *
   * One route: the two tabs (By Vendor · By Document) are `?tab=` query
   * parameters (ADR-0008, ADR-0021). **Not pinned** — the finance bottom bar
   * already carries Payments (pinned) and the cap is 4 with "More" at fourth;
   * pinning TCS Register would push Payments to the "More" drawer on a finance
   * user's phone, which is wrong.
   */
  tcsRegister: '/tcs-register',

  /**
   * --- feature 025 (025/design.md §6.1).
   *
   * **One route for the module, and NO nav entry anywhere** — the manager
   * ruled on 2026-10-21 that `nav-items.tsx` is not touched by 025 (§6.2,
   * rejecting the proposed `inbound` group). The module is reached from the
   * permission-filtered tab strip on the Goods Receipts list page, from the
   * vendor drawer's unbilled block, from the stock movements drawer's source
   * link, and from a deep link — **none of which ever went through the nav**.
   *
   * The two tabs (`?tab=challans` default | `open`), the entry drawer
   * (`?record=new`), the posted document (`?record=<id>`), a correction
   * (`&mode=edit`) and the version surfaces (`&versions=1`, `&version=<n>`)
   * are all query parameters over this one path (ADR-0008, ADR-0021).
   */
  deliveryChallans: '/delivery-challans',
  /**
   * **D-20 — the vendor statement's own standalone print page** (§6.1, S12,
   * AC-N7, AC-N8). It carries `?vendorId=&dateFrom=&dateTo=` and re-fetches
   * §4.5b itself, so a printed sheet is reproducible from its URL alone.
   *
   * **MUST be declared above `/delivery-challans/:id/print` in `router.tsx`,
   * and here it is a literal path above a parameterised one** — the frontend
   * twin of the backend's route-ordering hazard. Get it wrong and `statement`
   * binds as `:id`, the page fetches a challan by that id, and the statement
   * 404s while being perfectly well mounted.
   *
   * Same ADR-0021 exception and the same reason as the challan print page
   * below: the chrome must be **absent from the DOM**, not hidden by a print
   * rule.
   */
  deliveryChallanStatementPrint: '/delivery-challans/statement/print',
  /**
   * **A standalone page, not a drawer** — the one deliberate exception to
   * ADR-0021 in this feature, for 012's and 004's reason: AC-B2 requires the
   * chrome **absent from the DOM** rather than hidden, because a print
   * stylesheet over a full-viewport overlay is fragile exactly when it
   * matters.
   *
   * **Declared above the shell route — belt and braces, not the mechanism.**
   * React Router 7 scores `/delivery-challans/:id/print` above
   * `/delivery-challans/*`, and this module declares no `/:id` route at all,
   * so nothing can out-rank it. Order decides only in the one arrangement the
   * two shipped print routes document; **do not move this below the shell.**
   */
  deliveryChallanPrint: '/delivery-challans/:id/print',

  /**
   * --- feature 026 (026/design.md §6.1).
   *
   * **One route for the module, and NO nav entry anywhere** — the manager's
   * 2026-10-21 ruling that `nav-items.tsx` is not touched stands, and 026 does
   * not re-litigate it (DT-026-18). The module is reached from the
   * permission-filtered inbound tab strip 025 built, from a bill link on the
   * challan detail's settlement block, from the stock ledger's `vendor_bill`
   * source link, from 011's payment rows and from a deep link.
   *
   * The entry drawer (`?record=new`), the posted document (`?record=<id>`), a
   * correction (**`&billMode=edit`**) and the version surfaces (`&versions=1`,
   * `&version=<n>`) are all query parameters over this one path (ADR-0008,
   * ADR-0021).
   *
   * **`&billMode=edit`, not `&mode=edit`** — see `VENDOR_BILL_MODE_PARAM`: the
   * global `mode` key is `use-record-param`'s, and the Stock page mounts its
   * movements drawer on `record.mode === 'view'`, so writing `mode=edit` from a
   * document drawer destroys the whole drawer stack. 026 is the third module to
   * need its own key and the first to inherit the rule rather than discover it.
   */
  vendorBills: '/vendor-bills',
  /**
   * **A standalone page, not a drawer** — the one deliberate exception to
   * ADR-0021 in this feature, for 012's, 004's and 025's reason: AC-P4 needs
   * the chrome **absent from the DOM**, not hidden, because a print stylesheet
   * over a full-viewport overlay is fragile exactly when it matters.
   *
   * **Declared above the shell route in `router.tsx` — belt and braces, not
   * the mechanism.** React Router 7 ranks `/vendor-bills/:id/print` above
   * `/vendor-bills/*`, and this module declares no `/:id` route at all, so
   * nothing can out-rank it. Order decides only in the one arrangement the
   * shipped print routes document (a layout route at `/vendor-bills/:id` with
   * a nested `print` child); **do not move this below the shell.**
   */
  vendorBillPrint: '/vendor-bills/:id/print',
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * `/goods-receipts?record=<uuid>` — the posted document, as the drawer over
 * the list that it now is (ADR-0021).
 *
 * A helper rather than a template literal at each call site, so the shape of
 * this URL is written down once. In-app links point straight at the canonical
 * URL; `/goods-receipts/<uuid>` still resolves, via the redirect route, for
 * links that were shared before the rebuild.
 */
export function goodsReceiptPath(id: string): string {
  return `${ROUTES.goodsReceipts}?record=${encodeURIComponent(id)}`;
}

/** `/goods-receipts/<uuid>/print` — the standalone printable receipt (feature 012). */
export function goodsReceiptPrintPath(id: string): string {
  return `${ROUTES.goodsReceipts}/${encodeURIComponent(id)}/print`;
}

/**
 * `/purchase-orders?record=<uuid>` — the PO document, as the drawer over its
 * list that it is (ADR-0021).
 */
export function purchaseOrderPath(id: string): string {
  return `${ROUTES.purchaseOrders}?record=${encodeURIComponent(id)}`;
}

/** `/purchase-orders/<uuid>/print` — the standalone printable PO (feature 004). */
export function purchaseOrderPrintPath(id: string): string {
  return `${ROUTES.purchaseOrders}/${encodeURIComponent(id)}/print`;
}

/** `/purchase-returns?record=<uuid>` — a posted return, as the drawer over its list. */
export function purchaseReturnPath(id: string): string {
  return `${ROUTES.purchaseReturns}?record=${encodeURIComponent(id)}`;
}

/** `/payments?record=<uuid>` — a posted payment, as the drawer over the register. */
export function paymentPath(id: string): string {
  return `${ROUTES.payments}?record=${encodeURIComponent(id)}`;
}

/**
 * `/delivery-challans?record=<uuid>` — a posted challan, as the drawer over
 * its list (025/design.md §6.1).
 *
 * The canonical in-app link: used by the stock movements drawer's source
 * column and by the vendor drawer's unbilled block. There is no legacy
 * `/delivery-challans/<uuid>` path to redirect from — this module never had
 * pages.
 */
export function deliveryChallanPath(id: string): string {
  return `${ROUTES.deliveryChallans}?record=${encodeURIComponent(id)}`;
}

/** `/delivery-challans/<uuid>/print` — the standalone printable challan (AC-B2). */
export function deliveryChallanPrintPath(id: string): string {
  return `${ROUTES.deliveryChallans}/${encodeURIComponent(id)}/print`;
}

/**
 * `/delivery-challans?tab=statement` — the Vendor statement tab (D-20, S11),
 * optionally carrying the period and vendor it should open on.
 *
 * `?tab=statement` is written because the statement is **not** the page's
 * default tab; `challans` is, and that one writes no key at all (§6.1).
 */
export function challanStatementPath(params?: {
  vendorId: string;
  dateFrom: string;
  dateTo: string;
}): string {
  const search = new URLSearchParams({ tab: 'statement' });
  if (params !== undefined) {
    search.set('vendorId', params.vendorId);
    search.set('dateFrom', params.dateFrom);
    search.set('dateTo', params.dateTo);
  }
  return `${ROUTES.deliveryChallans}?${search.toString()}`;
}

/**
 * `/delivery-challans/statement/print?vendorId=…&dateFrom=…&dateTo=…` — the
 * vendor statement's printable sheet (D-20, AC-N8).
 *
 * The whole query is in the URL, and that is the requirement rather than a
 * convenience: the sheet gets **signed by both parties**, so the page it was
 * printed from has to be reachable again from the link alone. Every value is
 * encoded, because a vendor id is a uuid and the two dates are plant days —
 * none of which is free text today, and all of which arrive from a URL.
 */
export function challanStatementPrintPath(params: {
  vendorId: string;
  dateFrom: string;
  dateTo: string;
}): string {
  const search = new URLSearchParams({
    vendorId: params.vendorId,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
  });
  return `${ROUTES.deliveryChallanStatementPrint}?${search.toString()}`;
}

/**
 * `/delivery-challans?tab=open` — the open-challan register (Story D).
 *
 * `?tab=challans` is the module's **default** tab and therefore writes no key
 * at all, which is why there is no helper for it: a default link is a clean
 * link (§6.1, 010's precedent).
 */
export function openChallansPath(): string {
  return `${ROUTES.deliveryChallans}?tab=open`;
}

/**
 * `/vendor-bills?record=<uuid>` — a posted bill, as the drawer over its list
 * (026/design.md §6.1).
 *
 * The canonical in-app link, and it has four callers rather than one: the
 * challan detail's settlement block, the stock movements drawer's `vendor_bill`
 * source link, 011's payment rows and the bill list itself. Each of them checks
 * `vendor_bill.read` **before rendering a link** (AC-P6) — the link is not a
 * permission mechanism, but a control the reader cannot use must not be
 * offered.
 */
export function vendorBillPath(id: string): string {
  return `${ROUTES.vendorBills}?record=${encodeURIComponent(id)}`;
}

/** `/vendor-bills/<uuid>/print` — the standalone printable bill (AC-P4). */
export function vendorBillPrintPath(id: string): string {
  return `${ROUTES.vendorBills}/${encodeURIComponent(id)}/print`;
}

/**
 * `/production-runs?record=<uuid>` — a posted run, as the drawer over its list
 * (005/design.md §6.1). Linked from the plan's progress block and from the
 * stock movements drawer's source document.
 */
export function productionRunPath(id: string): string {
  return `${ROUTES.productionRuns}?record=${encodeURIComponent(id)}`;
}
